import { createStore, applyMiddleware } from "redux";
import thunk from "redux-thunk";
import localforage from "localforage";

import { Logger } from "../utilities/Logger";
import {
  persistTransforms,
  rootReducer,
  asyncReducers,
  asyncTransforms,
  createAppReducer
} from "../redux/reducers/RootReducer";
import { persistStore, persistReducer, getStoredState } from "redux-persist";
import { setStore, setPersistor, store, persistor } from ".";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { getTokenForViewMode, getSWConsts } from "../utilities/Common";
import {
  getPortfoliosStorePortfolioSessionUserId,
  getPortfoliosDataStorageKey,
  LAST_FETCH_MAX_AGE
} from "../redux/actions/Common";
import { displayTimezoneSelector } from "../redux/reducers/AuthReducer";
import { setDisplayTimezoneSelector } from "../utilities/Timezone";

setDisplayTimezoneSelector(displayTimezoneSelector);

const clearViewPersistData = (persistKey, tokenForViewMode) => {
  try {
    let viewUrlData = JSON.parse(localStorage.getItem("viewUrlData"));

    if (tokenForViewMode && (viewUrlData === null || !viewUrlData[persistKey])) {
      viewUrlData = { ...viewUrlData, [persistKey]: new Date().getTime() };
    }

    for (let key in viewUrlData) {
      if (new Date().getTime() - viewUrlData[key] > 3600000) {
        delete viewUrlData[key];
        if (key.startsWith("portfolios")) {
          localforage.removeItem(key);
          continue;
        }
        localforage.removeItem(`persist:${key}`);
      }
    }
    localStorage.setItem("viewUrlData", JSON.stringify(viewUrlData));
  } catch (e) {
    console.log(e);
  }

  return;
};

export const setupStore = async storage => {
  const tokenForViewMode = getTokenForViewMode();
  const version = "v65" + (tokenForViewMode || "");
  const persistKey = "cuberaroot" + version;
  const persistConfig = {
    key: persistKey,
    storage,
    transforms: persistTransforms(),
    stateReconciler: autoMergeLevel2,
    serialize: false,
    deserialize: false
  };
  const persistedReducer = persistReducer(persistConfig, rootReducer);

  const updatedPortfolioReducerTS = {
    portfolios: undefined
  };
  const pendingPortfolioWrites = new Map();

  const portfolioPersistMiddleware = storeApi => next => action => {
    const result = next(action);
    if (tokenForViewMode) return result;

    const state = storeApi.getState();
    for (const key in state) {
      if (!key.startsWith("portfolios")) continue;

      const newStatePortfolioTs = state[key].currentPortfolioCustodiansUpdatedTimestamp;
      const previousTs = updatedPortfolioReducerTS[key];
      if (newStatePortfolioTs === previousTs) continue;

      updatedPortfolioReducerTS[key] = newStatePortfolioTs;

      // Skip the first transition (undefined -> something): that's hydration, not
      // a user-driven change. Without this, an idle tab would flush its hydrated
      // state on close and clobber another tab's edits.
      if (previousTs === undefined) continue;

      const filteredPortfolioObj = state[key]["portfolios"];
      if (!filteredPortfolioObj) continue;

      const portfolioStorageKey = getPortfoliosDataStorageKey(getPortfoliosStorePortfolioSessionUserId());
      pendingPortfolioWrites.set(portfolioStorageKey, filteredPortfolioObj);
    }

    return result;
  };

  const middlewares = [thunk, portfolioPersistMiddleware];

  if (!Logger.reduxMiddleware() === true) {
    setStore(createStore(persistedReducer, applyMiddleware(...middlewares)));
  } else {
    setStore(createStore(persistedReducer, applyMiddleware(...middlewares, Logger.reduxMiddleware())));
  }

  setPersistor(persistStore(store, { manualPersist: true }));

  clearViewPersistData(persistKey, tokenForViewMode);
  if (!tokenForViewMode) {
    const persistDate = localStorage.getItem(persistKey);
    const currentTime = new Date().getTime();

    // If local data is older than 2 days purge local data and persist new data
    if (!persistDate || currentTime - new Date(persistDate).getTime() > LAST_FETCH_MAX_AGE) {
      persistor.purge();
    }

    localStorage.setItem(persistKey, new Date().toISOString());
  }
  persistor.persist();

  if (!tokenForViewMode) {
    const flushPendingPortfolioWritesToSW = () => {
      if (pendingPortfolioWrites.size === 0 || window.isSigningOut) return;
      const SW_CONSTS = getSWConsts();
      for (const [key, value] of pendingPortfolioWrites) {
        navigator.serviceWorker?.controller?.postMessage({
          type: SW_CONSTS.STORE_PORTFOLIO_DATA,
          key,
          value,
          SW_CONSTS
        });
      }
      pendingPortfolioWrites.clear();
    };

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushPendingPortfolioWritesToSW();
    });
    window.addEventListener("pagehide", flushPendingPortfolioWritesToSW);
  }

  store.injectReducers = reducers => {
    reducers = reducers.filter(item => !asyncReducers[item.key] === true);

    if (reducers.length === 0) {
      return;
    }

    reducers.forEach(item => {
      console.log("store add reducer with key", item.key);

      asyncReducers[item.key] = item.reducer;
      updatedPortfolioReducerTS[item.key] = undefined;
      asyncTransforms.push(item.transform);
    });

    const persistConfig = {
      key: persistKey,
      storage,
      transforms: persistTransforms(),
      stateReconciler: autoMergeLevel2,
      serialize: false,
      deserialize: false
    };

    createAppReducer();

    const persistedReducer = persistReducer(persistConfig, rootReducer);
    store.replaceReducer(persistedReducer);
    console.log("store replaceReducer");
    persistor.persist();
  };

  store.getStoredState = async () => {
    const persistConfig = {
      key: persistKey,
      storage,
      transforms: persistTransforms(),
      stateReconciler: autoMergeLevel2,
      serialize: false,
      deserialize: false
    };

    return await getStoredState(persistConfig);
  };
};
